import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Loader2, Mail, MailCheck, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, apiErrorCode } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCurrentPartner, type PartnerMe } from "@/hooks/useCurrentPartner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CodeStatusBadge } from "@/components/StatusBadge";
import { DateRangePicker, type DateRange } from "@/components/DateRangePicker";
import { EditorialButton, SearchInput } from "@/components/editorial";
import { ColumnInfo, DashCard, DashHeading, PillTabs } from "@/components/dashboard";
import { IssueCodeDialog } from "@/components/IssueCodeDialog";
import { formatCode, formatDateTZ, formatDuration, formatMoney } from "@/lib/format";
import { buildWelcomeMessage } from "@/lib/welcome-message";
import { cn } from "@/lib/utils";

type CodeItem = {
  code: string;
  duration_days: number;
  currency: string;
  price_cents: number;
  created_at: string;
  code_expires_at: string;
  redeemed_at: string | null;
  voided_at: string | null;
  voided_reason: string | null;
  sold_to_email: string | null;
  sold_to_name: string | null;
  email_sent_at: string | null;
  subscription_id: number | null;
  is_renewal: boolean;
  status: "active" | "redeemed" | "expired_unredeemed" | "voided";
};

type CodeList = { items: CodeItem[]; total: number };

const FILTERS = [
  { value: "", label: "Todos" },
  { value: "active", label: "Activos" },
  { value: "redeemed", label: "Canjeados" },
  { value: "expired_unredeemed", label: "Expirados" },
  { value: "voided", label: "Anulados" },
];

function defaultRange(): DateRange {
  const d = new Date();
  const from = new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
  const to = new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString().slice(0, 10);
  return { from, to };
}

function rangeToUtc(range: DateRange): { from: string; to: string } {
  const [fy, fm, fd] = range.from.split("-").map(Number);
  const [ty, tm, td] = range.to.split("-").map(Number);
  const fromLocal = new Date(fy, fm - 1, fd, 0, 0, 0, 0);
  const toLocal = new Date(ty, tm - 1, td, 23, 59, 59, 999);
  return { from: fromLocal.toISOString(), to: toLocal.toISOString() };
}

export default function CodesPage() {
  const { data: partner } = useCurrentPartner();
  const role = useAuth((s) => s.user?.role);
  const canWrite = role === "owner" || role === "staff";
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [search, setSearch] = useState("");
  const [range, setRange] = useState<DateRange>(defaultRange());

  const utc = rangeToUtc(range);

  const { data, isLoading } = useQuery<CodeList>({
    queryKey: ["codes", statusFilter, search, range.from, range.to],
    queryFn: async () => {
      const params: Record<string, string | number> = {
        limit: 200,
        from: utc.from,
        to: utc.to,
      };
      if (statusFilter) params.status = statusFilter;
      if (search.trim()) params.q = search.trim();
      const res = await api.get<CodeList>("/partner/codes", { params });
      return res.data;
    },
    enabled: !!partner,
  });

  if (!partner) return <div className="text-sm text-neutral-500">Cargando…</div>;

  return (
    <div>
      <DashHeading
        title="Códigos"
        description="Historial de códigos de activación PRO. Cada código se emite con un plan, se envía por email al comprador y se marca como canjeado cuando el usuario lo activa en la app NiuFit."
        actions={<DateRangePicker value={range} onChange={setRange} />}
      />

      {canWrite && (
        <div className="flex justify-end mb-4">
          <IssueCodeDialog partner={partner} />
        </div>
      )}

      <DashCard
        className="overflow-hidden"
        bodyClassName="p-0"
      >
        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-neutral-200">
          <PillTabs
            value={statusFilter}
            onChange={setStatusFilter}
            options={FILTERS}
          />
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Buscar por nombre o email…"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50/50 border-b border-neutral-200">
              <tr className="text-left text-xs font-medium text-neutral-600">
                <th className="px-6 py-3">Código</th>
                <th className="px-6 py-3">Comprador</th>
                <th className="px-6 py-3">Plan</th>
                <th className="px-6 py-3">Precio</th>
                <th className="px-6 py-3">Emitido</th>
                <th className="px-6 py-3">Vence</th>
                <th className="px-6 py-3">Correo</th>
                <th className="px-6 py-3">
                  <span className="inline-flex items-center gap-1.5">
                    Estado
                    <ColumnInfo
                      title="Estados de un código"
                      items={[
                        { label: "Activo", description: "Emitido y aún no canjeado. El comprador todavía puede activarlo en la app." },
                        { label: "Canjeado", description: "Un usuario ya lo activó — se creó su acceso PRO y cuenta como venta consumada." },
                        { label: "Expirado", description: "Pasó la fecha límite de canjeo sin ser usado. Sigue contando en el bruto facturable — el partner ya lo vendió." },
                        { label: "Anulado", description: "El partner lo anuló manualmente. Se resta del bruto facturable." },
                      ]}
                    />
                  </span>
                </th>
                {canWrite && (
                  <th className="px-6 py-3 text-right">
                    <span className="inline-flex items-center gap-1.5">
                      Acciones
                      <ColumnInfo
                        title="Acciones disponibles"
                        items={[
                          { label: "Reenviar / Enviar (columna Correo)", description: "Envía por email el código al comprador. El botón aparece bajo el estado del correo. Se puede reenviar varias veces si el cliente lo pierde." },
                          { label: "Anular", description: "Descarta un código activo. Se resta del bruto facturable y ya no puede ser canjeado. Requiere motivo para auditoría. Solo disponible mientras el código esté activo." },
                        ]}
                      />
                    </span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {isLoading && (
                <tr>
                  <td colSpan={canWrite ? 9 : 8} className="px-6 py-8 text-sm text-neutral-500">
                    Cargando…
                  </td>
                </tr>
              )}
              {data?.items.map((c) => (
                <tr key={c.code} className="group hover:bg-neutral-50 transition-colors">
                  <td className="px-6 py-3">
                    <CodeCell code={c} partner={partner} />
                  </td>
                  <td className="px-6 py-3">
                    {c.sold_to_name ? (
                      <div>
                        <div className="text-sm text-neutral-900">{c.sold_to_name}</div>
                        <div className="text-xs text-neutral-500">{c.sold_to_email}</div>
                      </div>
                    ) : (
                      <span className="text-neutral-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-3">
                    <div className="text-sm text-neutral-900">{formatDuration(c.duration_days)}</div>
                    <div className="text-xs text-neutral-500 tabular-nums">
                      {c.duration_days} días
                      {c.is_renewal && (
                        <span className="ml-1.5 text-neutral-400">· renovación</span>
                      )}
                      {c.subscription_id && !c.is_renewal && (
                        <span className="ml-1.5 text-neutral-400">· recurrente</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-3 font-medium tabular-nums text-sm text-neutral-900">
                    {formatMoney(c.price_cents, c.currency)}
                  </td>
                  <td className="px-6 py-3 text-xs text-neutral-600">
                    {formatDateTZ(c.created_at, partner.timezone)}
                  </td>
                  <td className="px-6 py-3 text-xs text-neutral-600">
                    {formatDateTZ(c.code_expires_at, partner.timezone)}
                  </td>
                  <td className="px-6 py-3">
                    <EmailCell
                      code={c.code}
                      sentAt={c.email_sent_at}
                      hasEmail={!!c.sold_to_email}
                      canResend={canWrite && c.status !== "voided"}
                    />
                  </td>
                  <td className="px-6 py-3">
                    <CodeStatusBadge status={c.status} />
                  </td>
                  {canWrite && (
                    <td className="px-6 py-3 text-right whitespace-nowrap">
                      {c.status === "active" && <VoidButton code={c.code} />}
                    </td>
                  )}
                </tr>
              ))}
              {data && data.items.length === 0 && (
                <tr>
                  <td colSpan={canWrite ? 9 : 8} className="px-6 py-10 text-center text-sm text-neutral-500">
                    {search.trim()
                      ? `Sin resultados para "${search.trim()}".`
                      : "Sin códigos en esta vista."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </DashCard>
    </div>
  );
}

/* ─────────────── Code cell con hover-to-copy ─────────────── */

function CodeCell({ code, partner }: { code: CodeItem; partner: PartnerMe }) {
  const [copied, setCopied] = useState<null | "code" | "welcome">(null);

  async function handleCopy(kind: "code" | "welcome") {
    const text =
      kind === "code"
        ? formatCode(code.code)
        : buildWelcomeMessage({
            code: code.code,
            sold_to_name: code.sold_to_name,
            duration_days: code.duration_days,
            price_cents: code.price_cents,
            currency: code.currency,
            code_expires_at: code.code_expires_at,
            partnerName: partner.name,
            partnerTimezone: partner.timezone,
          });
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setTimeout(() => setCopied(null), 1800);
    } catch {
      // Fallback silencioso — el usuario notará que no cambió el ícono
    }
  }

  return (
    <div className="flex flex-col items-start gap-1">
      <span className="font-mono-num tabular-nums text-[13px] text-neutral-900">
        {formatCode(code.code)}
      </span>
      <div className="inline-flex items-center gap-1 opacity-40 group-hover:opacity-100 transition-opacity">
        <button
          type="button"
          onClick={() => handleCopy("code")}
          title="Copiar código"
          aria-label="Copiar código"
          className={cn(
            "inline-flex items-center justify-center h-6 w-6 rounded border transition-colors",
            copied === "code"
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : "border-neutral-200 bg-white text-neutral-500 hover:text-neutral-900 hover:border-neutral-300"
          )}
        >
          {copied === "code" ? (
            <Check className="h-3 w-3" strokeWidth={2.5} />
          ) : (
            <Copy className="h-3 w-3" strokeWidth={2} />
          )}
        </button>
        <button
          type="button"
          onClick={() => handleCopy("welcome")}
          title="Copiar código con mensaje de bienvenida (listo para WhatsApp)"
          aria-label="Copiar código con bienvenida"
          className={cn(
            "inline-flex items-center justify-center h-6 w-6 rounded border transition-colors",
            copied === "welcome"
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : "border-neutral-200 bg-white text-neutral-500 hover:text-neutral-900 hover:border-neutral-300"
          )}
        >
          {copied === "welcome" ? (
            <Check className="h-3 w-3" strokeWidth={2.5} />
          ) : (
            <MessageCircle className="h-3 w-3" strokeWidth={2} />
          )}
        </button>
      </div>
    </div>
  );
}

/* ─────────────── Sub-componentes ─────────────── */

function EmailCell({
  code,
  sentAt,
  hasEmail,
  canResend,
}: {
  code: string;
  sentAt: string | null;
  hasEmail: boolean;
  canResend: boolean;
}) {
  if (!hasEmail) return <span className="text-neutral-400">—</span>;
  const alreadySent = !!sentAt;
  return (
    <div className="flex flex-col items-start gap-1">
      <span
        className={cn(
          "inline-flex items-center gap-1 border border-transparent px-2 py-0.5 text-[11px] font-medium",
          alreadySent ? "text-emerald-700" : "text-amber-700"
        )}
      >
        {alreadySent ? <MailCheck className="h-3 w-3" strokeWidth={2} /> : <Mail className="h-3 w-3" strokeWidth={2} />}
        {alreadySent ? "Enviado" : "Pendiente"}
      </span>
      {canResend && <ResendChip code={code} alreadySent={alreadySent} />}
    </div>
  );
}

function ResendChip({ code, alreadySent }: { code: string; alreadySent: boolean }) {
  const qc = useQueryClient();
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const mutation = useMutation({
    mutationFn: async () => (await api.post<{ email_sent: boolean }>(`/partner/codes/${code}/resend-email`)).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["codes"] });
      setMsg({ text: data.email_sent ? "Enviado" : "Falló", ok: data.email_sent });
      setTimeout(() => setMsg(null), 2500);
      if (data.email_sent) toast.success("Correo reenviado");
      else toast.error("No se pudo enviar el correo");
    },
    onError: () => {
      setMsg({ text: "Error", ok: false });
      setTimeout(() => setMsg(null), 2500);
      toast.error("Error al reenviar el correo");
    },
  });
  const Icon = mutation.isPending ? Loader2 : alreadySent ? MailCheck : Mail;
  const label = mutation.isPending
    ? "Enviando"
    : msg?.text ?? (alreadySent ? "Reenviar" : "Enviar");

  return (
    <button
      type="button"
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending}
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors",
        "disabled:opacity-60 disabled:cursor-not-allowed",
        msg && !msg.ok
          ? "border-destructive/40 text-destructive bg-destructive/[0.03]"
          : "border-neutral-200 text-neutral-700 bg-white hover:bg-neutral-50 hover:border-neutral-300"
      )}
    >
      <Icon className={cn("h-3 w-3", mutation.isPending && "animate-spin")} strokeWidth={2} />
      {label}
    </button>
  );
}

/* ─────────────── Row actions ─────────────── */

function VoidButton({ code }: { code: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: async () => (await api.post(`/partner/codes/${code}/void`, { reason })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["codes"] });
      qc.invalidateQueries({ queryKey: ["reports", "revenue"] });
      qc.invalidateQueries({ queryKey: ["revenue-report"] });
      setOpen(false);
      setReason("");
      toast.success("Código anulado");
    },
    onError: (e) => {
      const c = apiErrorCode(e);
      let msg = "No se pudo anular";
      if (c === "CODE_ALREADY_REDEEMED") msg = "El código ya fue canjeado";
      else if (c === "CODE_ALREADY_EXPIRED") msg = "El código expiró — ya no se puede anular";
      else if (c === "CODE_ALREADY_VOIDED") msg = "El código ya estaba anulado";
      setErr(msg);
      toast.error(msg);
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <EditorialButton variant="ghost" size="sm">Anular</EditorialButton>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold tracking-tight">
            Anular {formatCode(code)}
          </DialogTitle>
          <DialogDescription>
            El código dejará de ser válido. Ingresa un motivo para el registro de auditoría.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 mt-2">
          <Label htmlFor="reason">Motivo</Label>
          <Input id="reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={255} />
          {err && <p className="text-sm text-destructive">{err}</p>}
        </div>
        <DialogFooter className="mt-4 gap-2">
          <DialogClose asChild><EditorialButton variant="ghost">Cancelar</EditorialButton></DialogClose>
          <EditorialButton
            variant="primary"
            disabled={!reason.trim() || mutation.isPending}
            onClick={() => { setErr(null); mutation.mutate(); }}
          >
            {mutation.isPending ? "Anulando" : "Anular código"}
          </EditorialButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

